import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe2, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentLang = i18n.language?.startsWith('pt') ? 'pt-BR' : (i18n.language || '').split('-')[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const changeLang = async (code) => {
    i18n.changeLanguage(code);
    setOpen(false);

    // Save to user profile if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await axios.patch(`${API}/api/locale/user`, { locale: code, timezone: tz }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }
  };

  const currentInfo = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-muted-foreground hover:text-foreground"
        aria-label="Select language"
        data-testid="language-selector-btn"
      >
        <Globe2 className="h-4 w-4" />
        <span className="text-xs font-medium hidden sm:inline">{currentInfo.flag}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg z-50 overflow-hidden py-1" data-testid="language-dropdown" role="listbox">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLang(lang.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${currentLang === lang.code ? 'text-amber-500 font-medium' : 'text-foreground'}`}
              role="option"
              aria-selected={currentLang === lang.code}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.label}</span>
              {currentLang === lang.code && <Check className="h-3.5 w-3.5 text-amber-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LanguageBanner() {
  const { i18n, t } = useTranslation();
  const [show, setShow] = useState(false);
  const [detected, setDetected] = useState('');
  const dismissed = useRef(false);

  useEffect(() => {
    if (dismissed.current) return;

    // Check if user already made a choice (cookie exists)
    const hasCookie = document.cookie.includes('uclean_locale=');
    if (hasCookie) return;

    // Detect browser language
    const browserLangs = navigator.languages || [navigator.language];
    let browserLocale = '';
    const supported = { en: 'en', es: 'es', fr: 'fr', de: 'de', pt: 'pt-BR' };
    for (const bl of browserLangs) {
      const base = bl.split('-')[0].toLowerCase();
      if (supported[base]) { browserLocale = supported[base]; break; }
    }

    const currentLang = supported[(i18n.language || '').split('-')[0].toLowerCase()] || i18n.language;
    if (browserLocale && browserLocale !== currentLang && browserLocale !== 'pt-BR') {
      setDetected(browserLocale);
      setShow(true);
    }
  }, [i18n.language]);

  const handleSwitch = () => {
    i18n.changeLanguage(detected);
    setShow(false);
    dismissed.current = true;
  };

  const handleKeep = () => {
    // Save current language to cookie to avoid asking again
    const lng = i18n.language?.startsWith('pt') ? 'pt-BR' : i18n.language;
    i18n.changeLanguage(lng);
    setShow(false);
    dismissed.current = true;
  };

  if (!show) return null;

  const langName = LANGUAGES.find(l => l.code === detected)?.label || detected;

  return (
    <div className="bg-background/95 backdrop-blur-xl border-b border-amber-500/20 px-4 py-2.5 flex items-center justify-center gap-3 text-sm" data-testid="language-banner" role="alert">
      <span className="text-foreground">
        {t('locale.bannerDetected')} <strong>{langName}</strong>.
      </span>
      <button onClick={handleSwitch} className="px-3 py-1 rounded-full bg-amber-500 text-black text-xs font-semibold hover:bg-amber-400 transition-colors" data-testid="banner-switch">
        {t('locale.bannerSwitch')}
      </button>
      <button onClick={handleKeep} className="px-3 py-1 rounded-full border border-zinc-300 dark:border-zinc-600 text-xs font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors" data-testid="banner-keep">
        {t('locale.bannerKeep')}
      </button>
    </div>
  );
}
